/**
 * CountUp - proof-stat number that counts up once when scrolled into view.
 *
 * Parses a leading integer from the value ("24/7" -> counts 24, renders "/7"
 * statically; "18" -> counts 18). Non-numeric or tiny values just render.
 * Reduced motion / no leading number: static final value. The aria-label always
 * carries the final value; the animated digits are presentation-only.
 */

import { useEffect, useRef, useState } from 'react'
import { animate, useInView } from 'framer-motion'
import { useReducedMotion } from './useReducedMotion'

interface CountUpProps {
  value: string
  className?: string
}

export function CountUp({ value, className }: CountUpProps) {
  const match = value.match(/^(\d+)(.*)$/)
  const target = match ? parseInt(match[1], 10) : null
  const suffix = match ? match[2] : ''
  const prefersReducedMotion = useReducedMotion()
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  const [display, setDisplay] = useState(prefersReducedMotion || target === null || target <= 1 ? target : 0)

  useEffect(() => {
    if (prefersReducedMotion || target === null || target <= 1) return
    if (!inView) return
    const controls = animate(0, target, {
      duration: 0.8,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(Math.round(v)),
    })
    return () => controls.stop()
  }, [inView, target, prefersReducedMotion])

  if (target === null) {
    return <span ref={ref} className={className}>{value}</span>
  }

  return (
    <span ref={ref} className={className} aria-label={value} style={{ fontVariantNumeric: 'tabular-nums' }}>
      <span aria-hidden="true">{prefersReducedMotion || target <= 1 ? target : display}{suffix}</span>
    </span>
  )
}
